import axios from 'axios';
import config from '../config';

const API_URL = config.apiUrl;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('admin_refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh-token`, {
            refreshToken
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('admin_token', accessToken);
          localStorage.setItem('admin_refresh_token', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh_token');
          window.location.href = '/#/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        window.location.href = '/#/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Admin API endpoints
export const adminApi = {
  // Setup
  checkSetupRequired: () => api.get('/admin/setup-required'),
  initialSetup: (data) => api.post('/admin/setup', data),
  
  // Auth
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  
  // Metrics
  getMetrics: () => api.get('/admin/metrics'),
  
  // Issues
  getIssues: (params) => api.get('/admin/issues', { params }),
  getIssue: (id) => api.get(`/admin/issues/${id}`),
  createIssue: (data) => api.post('/admin/issues', data),
  updateIssue: (id, data) => api.patch(`/admin/issues/${id}`, data),
  analyzeIssue: (id) => api.post(`/admin/issues/${id}/analyze`),
  getOpenIssueCount: () => api.get('/admin/issues/open-count'),
  
  // AI PM
  runAnalysis: (data) => api.post('/admin/ai-pm/analyze', data),
  getReports: (params) => api.get('/admin/ai-pm/reports', { params }),
  getReport: (id) => api.get(`/admin/ai-pm/reports/${id}`),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  resendVerificationEmail: (id) => api.post(`/admin/users/${id}/resend-verification`),
  getActivationLink: (id) => api.get(`/admin/users/${id}/activation-link`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Companies
  getCompanies: (params) => api.get('/admin/companies', { params }),
  getCompany: (id) => api.get(`/admin/companies/${id}`),
  getCompanyCostAnalysis: (id, params) => api.get(`/admin/companies/${id}/cost-analysis`, { params }),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (id, data) => api.patch(`/admin/companies/${id}`, data),
  
  // Regions
  getRegions: (params) => api.get('/regions', { params }),
  getAvailableRegions: () => api.get('/regions/available'),
  getRegion: (id) => api.get(`/regions/${id}`),
  createRegion: (data) => api.post('/regions', data),
  updateRegion: (id, data) => api.patch(`/regions/${id}`, data),
  deleteRegion: (id) => api.delete(`/regions/${id}`),
  setDefaultRegion: (id) => api.post(`/regions/${id}/set-default`),
  
  // Warehouse Storage
  getWarehouseStorage: () => api.get('/warehouse/storage'),
  getWarehouseHistory: () => api.get('/warehouse/storage/history'),
  
  // Tickets
  getTickets: (params) => api.get('/tickets', { params }),
  getTicket: (id) => api.get(`/tickets/${id}`),
  updateTicket: (id, data) => api.patch(`/tickets/${id}`, data),
  addTicketMessage: (id, content, isAIGenerated) => api.post(`/tickets/${id}/messages`, { content, isAIGenerated }),
  closeTicket: (id) => api.post(`/tickets/${id}/close`),
  matchTicketWithIssues: (id) => api.post(`/tickets/${id}/match-issues`),
  generateAIResponse: (id) => api.post(`/tickets/${id}/ai-response`),
  analyzeTicket: (id) => api.post(`/tickets/${id}/analyze`, {}, { timeout: 60000 }),
  generateGuidedResponse: (id, context) => api.post(`/tickets/${id}/guided-response`, context, { timeout: 60000 }),
  getTicketStats: () => api.get('/tickets/stats'),
  getUnreadTicketCount: () => api.get('/tickets/unread-count'),
  getNotClosedTicketCount: () => api.get('/tickets/not-closed-count'),
  
  // Help Documentation (Admin)
  getHelpDocs: (params) => api.get('/help/admin/docs', { params }),
  getHelpDoc: (id) => api.get(`/help/admin/docs/${id}`),
  createHelpDoc: (data) => api.post('/help/admin/docs', data),
  updateHelpDoc: (id, data) => api.put(`/help/admin/docs/${id}`, data),
  deleteHelpDoc: (id) => api.delete(`/help/admin/docs/${id}`),
  reorderHelpDocs: (documents) => api.post('/help/admin/docs/reorder', { documents }),
  
  // AI Documentation Assistant
  analyzeDocumentation: () => api.post('/help/admin/ai/analyze', {}, { timeout: 60000 }),
  generateHelpDoc: (data) => api.post('/help/admin/ai/generate', data, { timeout: 120000 }),
  generateHelpDocsBatch: (documents) => api.post('/help/admin/ai/generate-batch', { documents }, { timeout: 300000 }),
  
  // Subscription Plans
  getSubscriptionPlans: () => api.get('/admin/subscriptions/plans'),
  seedSubscriptionData: () => api.post('/admin/subscriptions/seed'),
  getSubscriptionPlan: (id) => api.get(`/admin/subscriptions/plans/${id}`),
  updateSubscriptionPlan: (id, data) => api.patch(`/admin/subscriptions/plans/${id}`, data),
  
  // Currencies
  getCurrencies: () => api.get('/admin/subscriptions/currencies'),
  createCurrency: (data) => api.post('/admin/subscriptions/currencies', data),
  updateCurrency: (id, data) => api.patch(`/admin/subscriptions/currencies/${id}`, data),
  deleteCurrency: (id) => api.delete(`/admin/subscriptions/currencies/${id}`),
  setDefaultCurrency: (id) => api.post(`/admin/subscriptions/currencies/${id}/set-default`),
  
  // Credit Packs
  getCreditPacks: () => api.get('/admin/subscriptions/credit-packs'),
  getCreditPack: (id) => api.get(`/admin/subscriptions/credit-packs/${id}`),
  createCreditPack: (data) => api.post('/admin/subscriptions/credit-packs', data),
  updateCreditPack: (id, data) => api.patch(`/admin/subscriptions/credit-packs/${id}`, data),
  deleteCreditPack: (id) => api.delete(`/admin/subscriptions/credit-packs/${id}`),
  
  // AI Feature Costs
  getAIFeatureCosts: () => api.get('/admin/subscriptions/ai-costs'),
  getAIFeatureCost: (id) => api.get(`/admin/subscriptions/ai-costs/${id}`),
  updateAIFeatureCost: (id, data) => api.patch(`/admin/subscriptions/ai-costs/${id}`, data),
  
  // Additional Seats
  getAdditionalSeatsConfig: () => api.get('/admin/subscriptions/additional-seats'),
  updateAdditionalSeatsConfig: (data) => api.patch('/admin/subscriptions/additional-seats', data),
  
  // Company Credits
  getCompanyCredits: (companyId) => api.get(`/admin/subscriptions/company-credits/${companyId}`),
  adjustCompanyCredits: (companyId, data) => api.post(`/admin/subscriptions/company-credits/${companyId}/adjust`, data),
  getPostpaidReport: (companyId, params) => api.get(`/admin/subscriptions/company-credits/${companyId}/postpaid-report`, { params }),
  refreshCompanyCredits: (companyId) => api.post(`/admin/subscriptions/company-credits/${companyId}/refresh`),
  
  // Lemon Squeezy Settings
  getLemonSqueezySettings: () => api.get('/admin/subscriptions/lemon-squeezy-settings'),
  updateLemonSqueezySettings: (data) => api.patch('/admin/subscriptions/lemon-squeezy-settings', data),
  
  // Enterprise Payment
  createEnterprisePaymentLink: (data) => api.post('/admin/subscriptions/enterprise-payment-link', data),
  
  // Enterprise Inquiries
  getEnterpriseInquiries: (params) => api.get('/admin/enterprise/inquiries', { params }),
  getEnterpriseInquiryCounts: () => api.get('/admin/enterprise/inquiries/counts'),
  getEnterpriseInquiry: (id) => api.get(`/admin/enterprise/inquiries/${id}`),
  updateEnterpriseInquiry: (id, data) => api.patch(`/admin/enterprise/inquiries/${id}`, data),
  addEnterpriseMessage: (id, content) => api.post(`/admin/enterprise/inquiries/${id}/messages`, { content }),
  convertToCompany: (id, data) => api.post(`/admin/enterprise/inquiries/${id}/convert`, data),
  getAvailableAdmins: () => api.get('/admin/enterprise/admins'),
  
  // System Settings
  getSystemSettings: () => api.get('/admin/settings'),
  updateSystemSettings: (data) => api.patch('/admin/settings', data),
  
  // Invoices
  getInvoices: (params) => api.get('/admin/invoices', { params }),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  getInvoiceStats: () => api.get('/admin/invoices/stats'),
  getEligibleCompanies: () => api.get('/admin/invoices/eligible-companies'),
  generateInvoice: (data) => api.post('/admin/invoices/generate', data),
  generateMonthlyInvoices: (data) => api.post('/admin/invoices/generate-monthly', data),
  markInvoicePaid: (id, data) => api.patch(`/admin/invoices/${id}/mark-paid`, data),
  resendInvoice: (id, data) => api.post(`/admin/invoices/${id}/resend`, data),
  cancelInvoice: (id, data) => api.delete(`/admin/invoices/${id}`, { data }),
  downloadInvoicePDF: (id) => api.get(`/admin/invoices/${id}/pdf`, { responseType: 'blob' }),
  
  // SDK Configuration
  getSDKConfig: (companyId) => api.get(`/sdk/admin/${companyId}`),
  updateSDKConfig: (companyId, data) => api.put(`/sdk/admin/${companyId}`, data),
  regenerateSDKApiKey: (companyId) => api.post(`/sdk/admin/${companyId}/regenerate-key`),
  getSDKUsage: (companyId, params) => api.get(`/sdk/admin/${companyId}/usage`, { params }),
  testSDKConfig: (companyId, data) => api.post(`/sdk/admin/${companyId}/test`, data),
  
  // Referrals
  getReferrals: (params) => api.get('/referrals/admin/all', { params }),
  getReferralStats: (params) => api.get('/referrals/admin/stats', { params }),
  getReferralDetails: (id) => api.get(`/referrals/admin/${id}`),
  blockReferral: (id, reason) => api.post(`/referrals/admin/${id}/block`, { reason }),
  
  // Invite Codes
  getInviteCodeStats: () => api.get('/admin/invite-codes/stats'),
  getInviteCodes: (params) => api.get('/admin/invite-codes', { params }),
  getInviteCode: (id) => api.get(`/admin/invite-codes/${id}`),
  generateBatchCodes: (data) => api.post('/admin/invite-codes/batch', data),
  createIndividualCode: (data) => api.post('/admin/invite-codes/individual', data),
  deactivateInviteCode: (id) => api.patch(`/admin/invite-codes/${id}/deactivate`),
  reactivateInviteCode: (id) => api.patch(`/admin/invite-codes/${id}/reactivate`),
  getInviteCodeBatches: () => api.get('/admin/invite-codes/batches'),
  getInviteCodeBatch: (batchId) => api.get(`/admin/invite-codes/batches/${batchId}`),
  exportInviteCodes: (params) => api.get('/admin/invite-codes/export/csv', { params, responseType: 'blob' }),
  
  // Beta Configuration
  getBetaConfig: () => api.get('/admin/invite-codes/beta/config'),
  updateBetaConfig: (data) => api.put('/admin/invite-codes/beta/config', data),
  getBetaUsers: (params) => api.get('/admin/invite-codes/beta/users', { params }),
  runBetaExpirationCheck: () => api.post('/admin/invite-codes/beta/run-expiration-check'),
  
  // Mailbox (Sales & Support)
  getMailboxContacts: (mailboxType, params) => api.get(`/admin/mailbox/${mailboxType || ''}`, { params }),
  getMailboxStats: (mailboxType) => api.get(`/admin/mailbox/stats/${mailboxType || ''}`),
  getMailboxAdmins: () => api.get('/admin/mailbox/admins'),
  getMailboxContact: (id) => api.get(`/admin/mailbox/contact/${id}`),
  replyToMailboxContact: (id, message) => api.post(`/admin/mailbox/contact/${id}/reply`, { message }),
  updateMailboxContact: (id, data) => api.patch(`/admin/mailbox/contact/${id}`, data),
  getMailboxUnreadCount: (mailboxType) => api.get(`/admin/mailbox/stats/${mailboxType || ''}`).then(res => ({ data: { count: res.data.unreadCount || 0 } })),
  // Backward compatibility aliases
  getSalesContacts: (params) => api.get('/admin/mailbox/sales', { params }),
  getSalesContactStats: () => api.get('/admin/mailbox/stats/sales'),
  getSalesContactAdmins: () => api.get('/admin/mailbox/admins'),
  getSalesContact: (id) => api.get(`/admin/mailbox/contact/${id}`),
  replyToSalesContact: (id, message) => api.post(`/admin/mailbox/contact/${id}/reply`, { message }),
  updateSalesContact: (id, data) => api.patch(`/admin/mailbox/contact/${id}`, data),
  getSalesUnreadCount: () => api.get('/admin/mailbox/stats/sales').then(res => ({ data: { count: res.data.unreadCount || 0 } })),
  
  // Cache Management
  getCacheStats: () => api.get('/admin/cache/stats'),
  getCachedDataSources: (params) => api.get('/admin/cache/data-sources', { params }),
  clearDataSourceCache: (id) => api.delete(`/admin/cache/data-sources/${id}`)
};

export default api;

